import React from "react";

/**
 * Sophisticated animated digital globe.
 * Adds: pulsing nodes, sweeping rays, satellite, energy waves,
 * shooting stars and atmospheric glow.
 */
export default function AnimatedGlobe() {
  const dots = React.useMemo(() => {
    const out = [];
    const rows = 32;
    for (let r = 0; r < rows; r++) {
      const lat = (r / (rows - 1)) * Math.PI - Math.PI / 2;
      const y = Math.sin(lat);
      const ringRadius = Math.cos(lat);
      const cols = Math.max(6, Math.floor(ringRadius * 64));
      for (let c = 0; c < cols; c++) {
        const lon = (c / cols) * 2 * Math.PI;
        const x = ringRadius * Math.cos(lon);
        const z = ringRadius * Math.sin(lon);
        if (z > -0.25) out.push({ x, y, z });
      }
    }
    return out;
  }, []);

  const SIZE = 540;
  const R = 200;
  const CX = SIZE / 2;
  const CY = SIZE / 2;

  const project = (lat, lon) => ({
    x: CX + Math.cos(lat) * Math.cos(lon) * R,
    y: CY - Math.sin(lat) * R,
  });

  // Connection arcs
  const arcs = [
    { a: { lat: 0.55, lon: -1.2 }, b: { lat: 0.1, lon: 0.7 }, d: 0,   c: "#67E8F9" },
    { a: { lat: -0.4, lon: 0.3 },  b: { lat: 0.7, lon: -0.6 }, d: 1.5, c: "#A78BFA" },
    { a: { lat: 0.3, lon: -2.0 },  b: { lat: -0.6, lon: 1.4 }, d: 2.4, c: "#60A5FA" },
    { a: { lat: 0.5, lon: 1.7 },   b: { lat: -0.25, lon: -0.8 }, d: 3.6, c: "#F0ABFC" },
    { a: { lat: -0.7, lon: -1.4 }, b: { lat: 0.4, lon: 1.2 }, d: 4.7, c: "#67E8F9" },
  ];

  // Pulsing surface nodes (cities)
  const nodes = [
    { lat: 0.55, lon: -1.2 }, { lat: 0.1, lon: 0.7 }, { lat: -0.4, lon: 0.3 },
    { lat: 0.7, lon: -0.6 }, { lat: 0.3, lon: -2.0 }, { lat: -0.6, lon: 1.4 },
    { lat: 0.5, lon: 1.7 }, { lat: -0.25, lon: -0.8 }, { lat: -0.7, lon: -1.4 },
    { lat: 0.4, lon: 1.2 }, { lat: 0.05, lon: -0.4 }, { lat: -0.15, lon: 0.9 },
  ];

  return (
    <div className="globe-wrap globe-float" style={{ height: "100%" }}>
      <svg viewBox={`0 0 ${SIZE} ${SIZE}`} className="w-full h-full select-none" aria-hidden="true">
        <defs>
          <radialGradient id="planet-bg" cx="38%" cy="32%" r="75%">
            <stop offset="0%" stopColor="rgba(99,102,241,0.55)" />
            <stop offset="55%" stopColor="rgba(30,27,75,0.55)" />
            <stop offset="100%" stopColor="rgba(11,19,64,0.0)" />
          </radialGradient>
          <radialGradient id="planet-glow" cx="50%" cy="50%" r="62%">
            <stop offset="55%" stopColor="rgba(124,58,237,0)" />
            <stop offset="85%" stopColor="rgba(124,58,237,0.45)" />
            <stop offset="100%" stopColor="rgba(6,182,212,0.0)" />
          </radialGradient>
          <radialGradient id="halo" cx="50%" cy="50%" r="58%">
            <stop offset="70%" stopColor="rgba(34,211,238,0)" />
            <stop offset="92%" stopColor="rgba(167,139,250,0.4)" />
            <stop offset="100%" stopColor="rgba(96,165,250,0)" />
          </radialGradient>
          <linearGradient id="orbit-grad" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#06B6D4" />
            <stop offset="50%" stopColor="#8B5CF6" />
            <stop offset="100%" stopColor="#2563EB" />
          </linearGradient>
          <linearGradient id="sweep-grad" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="rgba(167,139,250,0)" />
            <stop offset="50%" stopColor="rgba(167,139,250,0.85)" />
            <stop offset="100%" stopColor="rgba(167,139,250,0)" />
          </linearGradient>
          <filter id="soft-blur"><feGaussianBlur stdDeviation="1.2" /></filter>
          <filter id="strong-glow"><feGaussianBlur stdDeviation="3.5" /></filter>
          <filter id="huge-glow"><feGaussianBlur stdDeviation="8" /></filter>
        </defs>

        {/* Outer atmospheric halo (animated breathing) */}
        <g style={{ transformOrigin: `${CX}px ${CY}px` }}>
          <circle cx={CX} cy={CY} r={R + 90} fill="url(#halo)">
            <animate attributeName="r" values={`${R + 80};${R + 110};${R + 80}`} dur="6s" repeatCount="indefinite" />
            <animate attributeName="opacity" values="0.6;0.95;0.6" dur="6s" repeatCount="indefinite" />
          </circle>
          <circle cx={CX} cy={CY} r={R + 50} fill="url(#planet-glow)" />
        </g>

        {/* Background stars (with parallax + twinkle) */}
        {Array.from({ length: 90 }).map((_, i) => {
          const sx = Math.random() * SIZE;
          const sy = Math.random() * SIZE;
          const sr = Math.random() * 0.9 + 0.2;
          return (
            <circle key={`s${i}`} cx={sx} cy={sy} r={sr} fill={i % 3 === 0 ? "#A5F3FC" : "#C4B5FD"}
                    opacity={Math.random() * 0.7 + 0.2} className="twinkle"
                    style={{ animationDelay: `${Math.random() * 3}s` }} />
          );
        })}

        {/* Shooting stars */}
        {[0, 1, 2].map((i) => (
          <line key={`shoot-${i}`}
                x1="0" y1={60 + i * 90}
                x2="0" y2={60 + i * 90}
                stroke="url(#sweep-grad)" strokeWidth="1.5">
            <animate attributeName="x1" values={`-50;${SIZE + 50}`} dur={`${7 + i * 2}s`} repeatCount="indefinite" begin={`${i * 4}s`} />
            <animate attributeName="x2" values={`-150;${SIZE - 50}`} dur={`${7 + i * 2}s`} repeatCount="indefinite" begin={`${i * 4}s`} />
            <animate attributeName="opacity" values="0;1;1;0" dur={`${7 + i * 2}s`} repeatCount="indefinite" begin={`${i * 4}s`} />
          </line>
        ))}

        {/* Planet sphere */}
        <circle cx={CX} cy={CY} r={R} fill="url(#planet-bg)" />

        {/* Equator + meridian wireframe rings */}
        <g style={{ transformOrigin: `${CX}px ${CY}px` }}>
          <ellipse cx={CX} cy={CY} rx={R} ry={R * 0.25} fill="none" stroke="#A78BFA" strokeWidth="0.6" opacity="0.4" />
          <ellipse cx={CX} cy={CY} rx={R * 0.7} ry={R} fill="none" stroke="#67E8F9" strokeWidth="0.5" opacity="0.35" />
          <ellipse cx={CX} cy={CY} rx={R * 0.35} ry={R} fill="none" stroke="#67E8F9" strokeWidth="0.4" opacity="0.25" />
        </g>

        {/* Dotted continents */}
        <g filter="url(#soft-blur)">
          {dots.map((d, i) => {
            const px = CX + d.x * R;
            const py = CY - d.y * R;
            const opacity = 0.2 + d.z * 0.7;
            const size = 0.9 + d.z * 1.3;
            const fill = d.z > 0.45 ? "#67E8F9" : "#A78BFA";
            return <circle key={i} cx={px} cy={py} r={size} fill={fill} opacity={opacity} />;
          })}
        </g>

        {/* Sweeping arc (radar-like) */}
        <g style={{ transformOrigin: `${CX}px ${CY}px` }}>
          <line x1={CX} y1={CY} x2={CX + R} y2={CY} stroke="url(#sweep-grad)" strokeWidth="3" opacity="0.75">
            <animateTransform attributeName="transform" type="rotate" from={`0 ${CX} ${CY}`} to={`360 ${CX} ${CY}`} dur="6s" repeatCount="indefinite" />
          </line>
        </g>

        {/* 3 orbit rings (rotating) */}
        <g style={{ transformOrigin: `${CX}px ${CY}px` }} className="globe-orbit globe-orbit-1">
          <ellipse cx={CX} cy={CY} rx={R + 40} ry={R - 70} fill="none" stroke="url(#orbit-grad)" strokeWidth="1.4" opacity="0.9" />
          <circle cx={CX + R + 40} cy={CY} r="5" fill="#67E8F9" filter="url(#strong-glow)" />
          <circle cx={CX + R + 40} cy={CY} r="2.2" fill="#fff" />
        </g>
        <g style={{ transformOrigin: `${CX}px ${CY}px`, transform: "rotate(60deg)" }} className="globe-orbit globe-orbit-2">
          <ellipse cx={CX} cy={CY} rx={R - 50} ry={R + 30} fill="none" stroke="url(#orbit-grad)" strokeWidth="1.2" opacity="0.75" />
          <circle cx={CX} cy={CY - (R + 30)} r="4" fill="#A78BFA" filter="url(#strong-glow)" />
          <circle cx={CX} cy={CY - (R + 30)} r="1.8" fill="#fff" />
        </g>
        <g style={{ transformOrigin: `${CX}px ${CY}px`, transform: "rotate(-30deg)" }} className="globe-orbit globe-orbit-3">
          <ellipse cx={CX} cy={CY} rx={R + 70} ry={R - 30} fill="none" stroke="url(#orbit-grad)" strokeWidth="1" opacity="0.6" />
          <circle cx={CX - R - 70} cy={CY} r="3.5" fill="#2563EB" filter="url(#strong-glow)" />
        </g>

        {/* 4th counter-orbit */}
        <g style={{ transformOrigin: `${CX}px ${CY}px`, transform: "rotate(110deg)" }} className="globe-orbit globe-orbit-2">
          <ellipse cx={CX} cy={CY} rx={R + 20} ry={R - 90} fill="none" stroke="url(#orbit-grad)" strokeWidth="0.9" opacity="0.5" />
          <circle cx={CX + R + 20} cy={CY} r="3" fill="#F0ABFC" filter="url(#strong-glow)" />
        </g>

        {/* Pulsing surface nodes */}
        {nodes.map((n, i) => {
          const p = project(n.lat, n.lon);
          return (
            <g key={`n${i}`}>
              <circle cx={p.x} cy={p.y} r="6" fill="none" stroke="#67E8F9" strokeWidth="1" opacity="0.7">
                <animate attributeName="r" from="3" to="14" dur="2.4s" repeatCount="indefinite" begin={`${i * 0.3}s`} />
                <animate attributeName="opacity" from="0.85" to="0" dur="2.4s" repeatCount="indefinite" begin={`${i * 0.3}s`} />
              </circle>
              <circle cx={p.x} cy={p.y} r="2.2" fill="#67E8F9" filter="url(#strong-glow)" />
              <circle cx={p.x} cy={p.y} r="1" fill="#fff" />
            </g>
          );
        })}

        {/* Connection arcs (animated dashes + traveling particle) */}
        {arcs.map((a, i) => {
          const p1 = project(a.a.lat, a.a.lon);
          const p2 = project(a.b.lat, a.b.lon);
          const cx = (p1.x + p2.x) / 2;
          const cy = (p1.y + p2.y) / 2 - 80;
          const dPath = `M ${p1.x} ${p1.y} Q ${cx} ${cy} ${p2.x} ${p2.y}`;
          const pathId = `arc-${i}`;
          return (
            <g key={`a${i}`}>
              <path id={pathId} d={dPath} fill="none" stroke={a.c} strokeWidth="1.6"
                    strokeDasharray="5 7" opacity="0.85">
                <animate attributeName="stroke-dashoffset" from="0" to="-50"
                         dur={`${3 + i * 0.7}s`} repeatCount="indefinite" begin={`${a.d}s`} />
              </path>
              {/* Traveling particle */}
              <circle r="3.2" fill="#fff" filter="url(#strong-glow)">
                <animateMotion dur={`${3 + i * 0.6}s`} repeatCount="indefinite" begin={`${a.d}s`}>
                  <mpath xlinkHref={`#${pathId}`} />
                </animateMotion>
              </circle>
            </g>
          );
        })}

        {/* Floating particles around the globe */}
        {Array.from({ length: 28 }).map((_, i) => {
          const angle = (i / 28) * Math.PI * 2;
          const dist = R + 80 + (i % 3) * 25;
          const x = CX + Math.cos(angle) * dist;
          const y = CY + Math.sin(angle) * dist * 0.72;
          return (
            <circle key={`pp${i}`} cx={x} cy={y} r={1.5 + (i % 2) * 0.7}
                    fill={i % 2 ? "#C4B5FD" : "#67E8F9"} opacity="0.85"
                    className="twinkle" style={{ animationDelay: `${i * 0.15}s` }} />
          );
        })}

        {/* Floating satellite (custom shape) */}
        <g style={{ transformOrigin: `${CX}px ${CY}px` }} className="globe-orbit globe-orbit-3">
          <g transform={`translate(${CX + R + 90}, ${CY})`}>
            <rect x="-8" y="-3" width="16" height="6" fill="#A78BFA" stroke="#fff" strokeWidth="0.5" rx="1.2" />
            <rect x="-14" y="-1" width="6" height="2" fill="#67E8F9" />
            <rect x="8" y="-1" width="6" height="2" fill="#67E8F9" />
            <circle cx="0" cy="0" r="1.6" fill="#fff" />
          </g>
        </g>

        {/* Pulse rings emanating from a fixed point */}
        {[0, 1, 2].map((i) => (
          <circle key={`pulse${i}`} cx={CX} cy={CY} r={R} fill="none" stroke="#A78BFA" strokeWidth="1.2" opacity="0">
            <animate attributeName="r" values={`${R};${R + 110}`} dur="4s" repeatCount="indefinite" begin={`${i * 1.3}s`} />
            <animate attributeName="opacity" values="0.65;0" dur="4s" repeatCount="indefinite" begin={`${i * 1.3}s`} />
          </circle>
        ))}
      </svg>
    </div>
  );
}
